import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { athenaEventEngine } from './AthenaEventEngine';
import { subscribeAthenaEvents } from './AthenaEventBus';
import { useAuth } from '../context/AuthContext';

const AthenaEventEngineContext = createContext(null);
const MAX_DECISIONS = 48;

/**
 * Central, diagnostics-only Event Engine wiring. Classification never invokes
 * a native plugin, sends a notification, opens a route, or calls the Worker.
 * User-visible actions continue through IntentRouter/ActionRouter instead.
 */
export function AthenaEventEngineProvider({ children }) {
  const { user } = useAuth();
  const [decisions, setDecisions] = useState([]);

  useEffect(() => {
    athenaEventEngine.clear();
    setDecisions([]);
  }, [user?.id]);

  useEffect(() => subscribeAthenaEvents((event) => {
    const decision = athenaEventEngine.classify(event, { allowProactiveCandidate: false });
    setDecisions((previous) => {
      const next = [...previous, decision];
      return next.length > MAX_DECISIONS ? next.slice(-MAX_DECISIONS) : next;
    });
  }), []);

  const value = useMemo(() => Object.freeze({
    // No event payloads are retained here; decisions contain only envelope
    // IDs/type classification/reason generated by the bounded engine.
    decisions: Object.freeze([...decisions]),
  }), [decisions]);

  return <AthenaEventEngineContext.Provider value={value}>{children}</AthenaEventEngineContext.Provider>;
}

export function useAthenaEventEngineDiagnostics() {
  const context = useContext(AthenaEventEngineContext);
  if (!context) throw new Error('useAthenaEventEngineDiagnostics must be used inside AthenaEventEngineProvider');
  return context;
}

